import {
    flexRender,
    getCoreRowModel,
    useReactTable,
    ColumnResizeMode,
} from "@tanstack/react-table";
import { SkuFoodType } from "../../../common/types";
import { useEffect, useMemo, useRef, useState } from "react";
import { useVirtualizer } from "@tanstack/react-virtual";
import { SkuFoodParamTable } from "../../../common/spin/optionsForSkuFood";

// Пропсы компонента: данные для таблицы и функция подгрузки
type SkuFoodTablePropsType = {
    data: SkuFoodType[];
    callBack: () => void;
};

// Основной компонент таблицы SKU Food
export const SkuFoodTable = ({ data, callBack }: SkuFoodTablePropsType) => {
    // Массив скрываемых колонок (не отображаются в таблице)
    const hidingColumns = [
        "id",
        "week_number",
        "start_date",
        "end_date",
        "level_2_samokat",
        "unic_status",
    ];

    // Формирование массива колонок для таблицы, исключая скрываемые
    const columns = useMemo(
        () =>
            Object.keys(data[0] || {})
                .map((el) => ({
                    header: el,
                    accessorKey: el,
                }))
                .filter((el) => !hidingColumns.includes(el.accessorKey)),
        [data]
    );

    // Восстановление сохраненных размеров колонок из localStorage
    const defaultColumnSizes = useMemo(() => {
        const savedSizes = localStorage.getItem("columnSizes");
        return savedSizes ? JSON.parse(savedSizes) : {};
    }, []);

    // Создание таблицы с помощью useReactTable
    const table = useReactTable({
        data,
        columns: columns.map((column) => {
            // Кастомная ячейка для колонки link
            if (column.header === "link") {
                return {
                    ...column,
                    cell: ({ row }: any) => {
                        const link = row.original.link;
                        return (
                            <a
                                href={link}
                                target="_blank"
                                rel="noopener noreferrer"
                                style={{ color: "#08A652", textDecoration: "underline" }}
                            >
                                link
                            </a>
                        );
                    },
                };
            }
            return column;
        }),
        getCoreRowModel: getCoreRowModel(),
        columnResizeMode: "onChange", // Режим изменения ширины колонок
        initialState: {
            columnSizing: defaultColumnSizes, // Восстановление сохраненных размеров
        },
        debugTable: true, // Включите для отладки
    });

    // Сохранение ширины колонок в localStorage при изменении
    useEffect(() => {
        const columnSizes = table.getState().columnSizing;
        localStorage.setItem("columnSizes", JSON.stringify(columnSizes));
    }, [table.getState().columnSizing]);

    // Виртуализация строк таблицы для производительности
    const parentRef = useRef<HTMLDivElement>(null);
    const rowVirtualizer = useVirtualizer({
        count: table.getRowModel().rows.length,
        getScrollElement: () => parentRef.current,
        estimateSize: () => 50,
        overscan: 20,
    });

    // Проверка, когда пользователь прокручивает вниз и близок к концу, вызываем подгрузку
    useEffect(() => {
        const lastItem = rowVirtualizer.getVirtualItems().at(-1);
        if (!lastItem) return;

        const isCloseToBottom = lastItem.index >= data.length - 10;
        if (isCloseToBottom) {
            callBack();
        }
    }, [rowVirtualizer.getVirtualItems(), data.length, callBack]);

    // Рендер таблицы с виртуализацией и ресайзом колонок
    return (
        <div
            ref={parentRef}
            style={{ height: "calc(100vh - 155px)", overflowY: "auto", position: "relative" }}
        >
            <table
                style={{
                    width: "100%",
                    borderCollapse: "collapse",
                    color: "white",
                    tableLayout: "fixed",
                }}
            >
                <thead style={{ color: "white" }}>
                    {table.getHeaderGroups().map((headerGroup) => (
                        <tr key={headerGroup.id}>
                            {headerGroup.headers.map((header) => {
                                // Рендер заголовка колонки с поддержкой ресайза
                                const render = flexRender(
                                    header.column.columnDef.header,
                                    header.getContext()
                                );
                                return (
                                    <th
                                        key={header.id}
                                        style={{
                                            width: header.getSize(), // Используем текущую ширину колонки
                                            background: "#08A652",
                                            padding: "8px",
                                            border: "1px solid black",
                                            whiteSpace: "nowrap",
                                            overflow: "hidden",
                                            textOverflow: "ellipsis",
                                            position: "sticky",
                                            top: -1,
                                            cursor: "col-resize", // Курсор для изменения ширины
                                            userSelect: "none", // Запрет выделения текста
                                        }}
                                        onMouseDown={header.getResizeHandler()} // Обработчик изменения ширины
                                        onTouchStart={header.getResizeHandler()} // Для мобильных устройств
                                    >
                                        {SkuFoodParamTable[render!.toString()]?.name}
                                        {/* Индикатор изменения ширины */}
                                        <div
                                            style={{
                                                position: "absolute",
                                                right: 0,
                                                top: 0,
                                                height: "100%",
                                                width: "4px",
                                                backgroundColor: "rgba(0, 0, 0, 0.5)",
                                                cursor: "col-resize",
                                            }}
                                        />
                                    </th>
                                );
                            })}
                        </tr>
                    ))}
                </thead>
                <tbody>
                    {/* Фикс пустого пространства сверху для виртуализации */}
                    <tr>
                        <td
                            style={{
                                height: rowVirtualizer.getVirtualItems()[0]?.start || 0,
                                padding: 0,
                            }}
                            colSpan={columns.length}
                        ></td>
                    </tr>

                    {rowVirtualizer.getVirtualItems().map((virtualRow) => {
                        const row = table.getRowModel().rows[virtualRow.index];
                        return (
                            <tr key={row.id} style={{ height: "20px" }}>
                                {row.getVisibleCells().map((cell) => (
                                    <td
                                        key={cell.id}
                                        style={{
                                            padding: "8px",
                                            border: "1px solid #ddd",
                                            whiteSpace: "nowrap",
                                            overflow: "hidden",
                                            textOverflow: "ellipsis",
                                        }}
                                    >
                                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                                    </td>
                                ))}
                            </tr>
                        );
                    })}
                </tbody>
            </table>
        </div>
    );
};
